import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  X,
  Play,
  MessageCircle,
  Brain,
  Library,
  Lightbulb,
  Plug,
  Landmark,
  Send,
  Bot,
  User,
  BarChart3,
  Shield,
  Zap,
  Eye,
  Bell,
  Target,
  Radio,
  Volume2,
  Mic,
  Sparkles,
  Lock,
  TrendingUp,
  Cpu,
  Server,
  Database,
  Activity,
  Palette,
  Calendar,
  BookOpen,
  Users,
  Smartphone,
  Globe,
  Monitor,
  Rocket,
  Newspaper,
  DollarSign,
  GitBranch,
  Layers,
  CreditCard,
  Wifi,
  Wrench,
} from 'lucide-react';
import SEO from '../components/SEO';

/* ═══════════════════ ANIMATIONS ═══════════════════ */

const STYLE_ID = 'rel-v2';

const injectStyles = () => {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes rel-fade-up {
      from { opacity: 0; transform: translateY(40px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes rel-gradient {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes rel-float-1 {
      0%, 100% { transform: translate(0, 0); }
      50% { transform: translate(20px, -15px); }
    }
    @keyframes rel-float-2 {
      0%, 100% { transform: translate(0, 0); }
      50% { transform: translate(-15px, 10px); }
    }
    @keyframes rel-typing {
      0%, 100% { opacity: 1; }
      50% { opacity: 0; }
    }
    @keyframes rel-pulse-dot {
      0%, 100% { transform: scale(1); opacity: 0.7; }
      50% { transform: scale(1.5); opacity: 1; }
    }
    @keyframes rel-scan-line {
      0% { top: 0%; }
      100% { top: 100%; }
    }
    @keyframes rel-bar-grow {
      from { transform: scaleY(0); }
      to { transform: scaleY(1); }
    }
    .rel-reveal { opacity: 0; transform: translateY(40px); }
    .rel-visible { animation: rel-fade-up 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
    .rel-delay-1.rel-visible { animation-delay: 0.1s; }
    .rel-delay-2.rel-visible { animation-delay: 0.2s; }
    .rel-delay-3.rel-visible { animation-delay: 0.3s; }
    .rel-gradient-text {
      background: linear-gradient(135deg, #2dd4bf, #818cf8, #f472b6, #2dd4bf);
      background-size: 300% 300%;
      animation: rel-gradient 8s ease infinite;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .rel-orb-1 { animation: rel-float-1 20s ease-in-out infinite; }
    .rel-orb-2 { animation: rel-float-2 25s ease-in-out infinite; }
    .rel-typing-cursor { animation: rel-typing 1s ease-in-out infinite; }
    .rel-nav-active {
      color: #fff !important;
      background: rgba(255,255,255,0.06);
    }
    .rel-scan::after {
      content: '';
      position: absolute;
      left: 0; right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, #2dd4bf40, transparent);
      animation: rel-scan-line 3s ease-in-out infinite;
    }
  `;
  document.head.appendChild(s);
};

/* ═══════════════════ HOOKS ═══════════════════ */

const useReveal = () => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('rel-visible');
          e.target.querySelectorAll('.rel-reveal').forEach((c) => c.classList.add('rel-visible'));
        }
      }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
};

const useActiveSection = (ids: string[]) => {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) setActive(visible[0].target.id);
      },
      { threshold: 0.3, rootMargin: '-80px 0px -50% 0px' }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [ids]);
  return active;
};

const useCounter = (target: number, visible: boolean) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const step = (t: number) => {
      if (!start) start = t;
      const p = Math.min((t - start) / 1500, 1);
      setVal(Math.floor((1 - Math.pow(1 - p, 4)) * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [visible, target]);
  return val;
};

/* ═══════════════════ UI MOCKUPS ═══════════════════ */

/** Chat interface mockup */
const ChatMockup: React.FC = () => (
  <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
    {/* Title bar */}
    <div className="px-4 py-3 border-b border-white/[0.04] flex items-center gap-2">
      <div className="w-3 h-3 rounded-full bg-red-500/60" />
      <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
      <div className="w-3 h-3 rounded-full bg-green-500/60" />
      <span className="ml-2 text-xs text-slate-500 font-medium">Expert Chat</span>
      <div className="ml-auto flex gap-1">
        <span className="px-2 py-0.5 rounded text-[10px] bg-teal-500/10 text-teal-400 font-medium">AI Powered</span>
      </div>
    </div>
    {/* Messages */}
    <div className="p-4 space-y-4 min-h-[280px]">
      <div className="flex gap-3 items-start">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0"><User size={12} className="text-white" /></div>
        <div className="bg-white/[0.04] rounded-xl rounded-tl-sm px-4 py-2.5 max-w-[80%]">
          <p className="text-sm text-slate-300">Should I buy GOLD right now? What's your analysis?</p>
        </div>
      </div>
      <div className="flex gap-3 items-start">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center flex-shrink-0"><Bot size={12} className="text-white" /></div>
        <div className="space-y-2 max-w-[85%]">
          {/* Tool pills */}
          <div className="flex flex-wrap gap-1.5">
            {['Market Data', 'S/R Zones', 'Portfolio', 'Risk Rules'].map((t) => (
              <span key={t} className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-teal-500/10 text-teal-400 border border-teal-500/10">
                {t} <CheckCircle2 size={8} className="inline ml-0.5" />
              </span>
            ))}
          </div>
          <div className="bg-white/[0.04] rounded-xl rounded-tl-sm px-4 py-2.5">
            <p className="text-sm text-slate-300">
              <span className="text-teal-400 font-semibold">XAUUSD @ $2,347.50</span> — Price is testing the daily S/R zone at $2,345. Your strategy requires zone retest + MTF trend alignment.
            </p>
            <p className="text-sm text-slate-400 mt-2">H4 trend: <span className="text-green-400">Bullish</span> | ATR: 18.4 | Risk: 1.2% of account</p>
          </div>
        </div>
      </div>
      {/* Input */}
      <div className="flex items-center gap-2 mt-4 bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-2.5">
        <span className="text-sm text-slate-600 flex-1">Ask about any asset...</span>
        <Send size={14} className="text-teal-500" />
      </div>
    </div>
  </div>
);

/** Strategy extraction pipeline mockup */
const PipelineMockup: React.FC = () => (
  <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
    <div className="px-4 py-3 border-b border-white/[0.04] flex items-center gap-2">
      <Library size={14} className="text-violet-400" />
      <span className="text-xs text-slate-500 font-medium">Strategy Extraction</span>
    </div>
    <div className="p-4 space-y-4">
      {/* URL input */}
      <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg px-4 py-2.5 flex items-center gap-2">
        <span className="text-sm text-slate-500">https://youtube.com/watch?v=</span>
        <span className="text-sm text-violet-400">ICX_trading_strategy</span>
      </div>
      {/* Pipeline steps */}
      {[
        { label: 'Download', status: 'done', detail: '1080p, 14:32' },
        { label: 'Transcribe', status: 'done', detail: 'Audio to text' },
        { label: 'Frame Analysis', status: 'done', detail: '24 key frames extracted' },
        { label: 'Strategy Extract', status: 'done', detail: 'AI extraction' },
        { label: 'Scoring', status: 'done', detail: '87/100 completeness' },
      ].map((step) => (
        <div key={step.label} className="flex items-center gap-3">
          <div className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center">
            <CheckCircle2 size={12} className="text-violet-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white font-medium">{step.label}</span>
              <span className="text-[11px] text-slate-600">{step.detail}</span>
            </div>
            <div className="h-1 bg-white/[0.04] rounded-full mt-1.5 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-violet-500 to-violet-400 rounded-full" style={{ width: '100%' }} />
            </div>
          </div>
        </div>
      ))}
      {/* Result card */}
      <div className="bg-violet-500/[0.04] border border-violet-500/10 rounded-xl p-4 mt-2">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-white">ICX Breakout Strategy</span>
          <span className="px-2 py-0.5 rounded-full text-[10px] bg-violet-500/20 text-violet-300 font-medium">87/100</span>
        </div>
        <p className="text-xs text-slate-500">Entries: Zone retest + volume spike | Timeframes: M15, H1 | Risk: 1% per trade</p>
      </div>
    </div>
  </div>
);

/** Learning pyramid visual */
const LearningMockup: React.FC = () => (
  <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
    <div className="px-4 py-3 border-b border-white/[0.04] flex items-center gap-2">
      <Lightbulb size={14} className="text-amber-400" />
      <span className="text-xs text-slate-500 font-medium">Knowledge Layers</span>
    </div>
    <div className="p-6 flex flex-col items-center gap-2">
      {[
        { w: '45%', label: 'Strategy', sub: 'ICX Zone Retest: use 2:1 RR', color: 'amber', opacity: '1' },
        { w: '60%', label: 'Symbol', sub: 'GOLD: prefer H4 timeframe', color: 'amber', opacity: '0.8' },
        { w: '78%', label: 'Domain', sub: 'Forex: always check DXY correlation', color: 'amber', opacity: '0.6' },
        { w: '95%', label: 'User', sub: 'Max 2% risk per trade, no martingale', color: 'amber', opacity: '0.4' },
      ].map((l) => (
        <div
          key={l.label}
          style={{ width: l.w }}
          className="py-3 px-4 rounded-xl bg-amber-500/[0.06] border border-amber-500/[0.1] text-center transition-all hover:bg-amber-500/[0.1]"
        >
          <p className="text-sm font-semibold text-amber-300">{l.label}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">{l.sub}</p>
        </div>
      ))}
      {/* Correction example */}
      <div className="w-full mt-4 bg-white/[0.02] border border-white/[0.04] rounded-xl p-3">
        <div className="flex items-start gap-2">
          <User size={12} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-slate-400"><span className="text-white">"Actually, I use 2:1 RR on gold, not 3:1"</span></p>
        </div>
        <div className="flex items-start gap-2 mt-2">
          <Bot size={12} className="text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-slate-400">Learned: GOLD risk:reward updated to <span className="text-amber-300">2:1</span> at Symbol level</p>
        </div>
      </div>
    </div>
  </div>
);

/** MCP integration diagram */
const MCPMockup: React.FC = () => (
  <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
    <div className="px-4 py-3 border-b border-white/[0.04] flex items-center gap-2">
      <Plug size={14} className="text-cyan-400" />
      <span className="text-xs text-slate-500 font-medium">MCP Connections</span>
    </div>
    <div className="p-5 space-y-6">
      {/* Clients */}
      {['Claude Desktop', 'Cursor IDE', 'Claude Code'].map((client, i) => (
        <div key={client} className="flex items-center gap-4">
          <div className="w-32 text-right">
            <span className="text-sm font-medium text-white">{client}</span>
          </div>
          <div className="flex-1 relative h-px bg-gradient-to-r from-cyan-500/30 via-cyan-500/10 to-transparent">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-cyan-400" />
            <div className="absolute left-1/3 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-cyan-400/50" style={{ animationDelay: `${i * 0.2}s` }} />
          </div>
          <div className="text-right">
            <span className="text-[10px] text-slate-600 font-mono">{['connected', 'connected', 'connected'][i]}</span>
          </div>
        </div>
      ))}
      {/* Tool registry */}
      <div className="bg-cyan-500/[0.04] border border-cyan-500/10 rounded-xl p-4">
        <p className="text-xs text-cyan-400 font-semibold mb-3">13 Tools Available</p>
        <div className="flex flex-wrap gap-1.5">
          {['get_portfolio', 'get_market_data', 'get_sr_zones', 'get_risk_rules', 'get_strategies', 'get_trade_history', 'get_signals', 'analyze_asset', 'get_positions', 'get_watchlist', 'search_web', 'get_news', 'get_indicators'].map((tool) => (
            <span key={tool} className="px-2 py-1 rounded text-[10px] font-mono bg-white/[0.03] text-slate-500 border border-white/[0.04]">{tool}</span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/** Banker monitoring dashboard mockup */
const BankerMockup: React.FC = () => (
  <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
    <div className="px-4 py-3 border-b border-white/[0.04] flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Landmark size={14} className="text-rose-400" />
        <span className="text-xs text-slate-500 font-medium">Investment Banker</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
        <span className="text-[10px] text-slate-600">Monitoring</span>
      </div>
    </div>
    <div className="p-4 space-y-3">
      {/* Position alerts */}
      <div className="space-y-2">
        <p className="text-[10px] text-slate-600 font-semibold uppercase tracking-wider">Position Oversight</p>
        {[
          { sym: 'XAUUSD', alert: 'URGENT', desc: 'Approaching S/R zone $2,360', color: 'red' },
          { sym: 'EURUSD', alert: 'ADVISORY', desc: 'ATR distance > 2.0', color: 'yellow' },
          { sym: 'NAS100', alert: 'SILENT', desc: 'In profit, trailing stop active', color: 'green' },
        ].map((p) => (
          <div key={p.sym} className="flex items-center gap-3 bg-white/[0.02] border border-white/[0.04] rounded-lg px-3 py-2">
            <span className={`w-1.5 h-5 rounded-full bg-${p.color}-500`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-white">{p.sym}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold bg-${p.color}-500/20 text-${p.color}-400`}>{p.alert}</span>
              </div>
              <p className="text-[11px] text-slate-500 truncate">{p.desc}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Opportunity */}
      <div className="space-y-2">
        <p className="text-[10px] text-slate-600 font-semibold uppercase tracking-wider">New Lead</p>
        <div className="bg-rose-500/[0.04] border border-rose-500/10 rounded-xl p-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-semibold text-white">GBPJPY Long</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] bg-rose-500/20 text-rose-300 font-bold">82%</span>
          </div>
          <p className="text-[11px] text-slate-500">Analyst + Banker convergence. Zone retest confirmed on H4.</p>
          <div className="flex gap-2 mt-2">
            <button className="px-3 py-1 rounded-lg text-[10px] font-semibold bg-teal-500/20 text-teal-400">Approve</button>
            <button className="px-3 py-1 rounded-lg text-[10px] font-semibold bg-white/[0.04] text-slate-500">Reject</button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/** Market intelligence mockup */
const IntelMockup: React.FC = () => (
  <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
    <div className="px-4 py-3 border-b border-white/[0.04] flex items-center gap-2">
      <Brain size={14} className="text-indigo-400" />
      <span className="text-xs text-slate-500 font-medium">Trade Analysis</span>
    </div>
    <div className="p-4 space-y-3">
      {/* Asset header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-lg font-bold text-white">XAUUSD</p>
          <p className="text-xs text-slate-500">Gold Spot</p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-green-400">$2,347.50</p>
          <p className="text-xs text-green-400">+0.34%</p>
        </div>
      </div>
      {/* Mini chart bars */}
      <div className="flex items-end gap-1 h-16 pt-2">
        {[40, 55, 45, 60, 50, 70, 65, 80, 75, 85, 70, 90, 85, 78, 82, 95, 88, 92].map((h, i) => (
          <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-indigo-500/40 to-indigo-400/20" style={{ height: `${h}%` }} />
        ))}
      </div>
      {/* Analysis grid */}
      <div className="grid grid-cols-2 gap-2">
        {[
          { label: 'Signal', value: 'BUY', color: 'green' },
          { label: 'Confidence', value: '84%', color: 'teal' },
          { label: 'Risk/Reward', value: '2.4:1', color: 'indigo' },
          { label: 'Portfolio Risk', value: '1.2%', color: 'amber' },
        ].map((m) => (
          <div key={m.label} className="bg-white/[0.02] border border-white/[0.04] rounded-lg px-3 py-2">
            <p className="text-[10px] text-slate-600">{m.label}</p>
            <p className={`text-sm font-bold text-${m.color}-400`}>{m.value}</p>
          </div>
        ))}
      </div>
      {/* S/R Zones */}
      <div className="bg-white/[0.02] border border-white/[0.04] rounded-lg px-3 py-2">
        <p className="text-[10px] text-slate-600 mb-1">Key Levels</p>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-red-400">R: $2,365</span>
          <span className="text-slate-600">|</span>
          <span className="text-white">Price: $2,347</span>
          <span className="text-slate-600">|</span>
          <span className="text-green-400">S: $2,330</span>
        </div>
      </div>
    </div>
  </div>
);

/** Voice & Avatar mockup */
const VoiceAvatarMockup: React.FC = () => (
  <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
    <div className="px-4 py-3 border-b border-white/[0.04] flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Volume2 size={14} className="text-emerald-400" />
        <span className="text-xs text-slate-500 font-medium">Voice Conversation</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-[10px] text-slate-600">Live</span>
      </div>
    </div>
    <div className="p-4 space-y-4">
      {/* Avatar character */}
      <div className="flex flex-col items-center py-3">
        <div className="relative">
          <div className="w-24 h-32 rounded-2xl bg-gradient-to-b from-emerald-500/20 to-emerald-500/5 border border-emerald-500/20 flex flex-col items-center justify-center overflow-hidden">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400/40 to-emerald-600/20 border border-emerald-500/30 mb-1 flex items-center justify-center">
              <div className="w-3 h-1 rounded-full bg-emerald-400/60" />
            </div>
            <div className="w-16 h-14 rounded-t-xl bg-gradient-to-b from-slate-700/40 to-slate-800/20 border-x border-t border-white/[0.06] mt-1">
              <div className="w-4 h-4 mx-auto mt-1 rotate-45 border-l-2 border-t-2 border-emerald-500/30" />
            </div>
          </div>
          <div className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/20">
            <span className="text-[9px] text-emerald-400 font-bold">Speaking</span>
          </div>
        </div>
        <span className="text-sm text-white font-medium mt-3">Charles</span>
        <span className="text-[10px] text-slate-600">Market Analyst</span>
      </div>
      {/* Audio waveform */}
      <div className="flex items-center justify-center gap-[3px] h-8">
        {[35, 55, 75, 95, 80, 60, 85, 70, 50, 90, 65, 45, 80, 55, 70].map((h, i) => (
          <div
            key={i}
            className="w-1 rounded-full bg-gradient-to-t from-emerald-500/40 to-emerald-400/60"
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
      {/* Voice conversation flow */}
      <div className="space-y-2">
        {[
          { role: 'user', text: '"What\'s happening with Gold?"', Icon: Mic },
          { role: 'ai', text: '"Gold is testing resistance at $2,365..."', Icon: Volume2 },
        ].map((msg, i) => (
          <div key={i} className="flex items-center gap-2 bg-white/[0.02] border border-white/[0.04] rounded-lg px-3 py-2">
            <msg.Icon size={12} className={msg.role === 'user' ? 'text-blue-400' : 'text-emerald-400'} />
            <span className="text-xs text-slate-400">{msg.text}</span>
          </div>
        ))}
      </div>
      {/* Push to talk button */}
      <div className="flex flex-col items-center gap-2">
        <div className="w-14 h-14 rounded-full bg-emerald-500/20 border-2 border-emerald-500/30 flex items-center justify-center">
          <Mic size={20} className="text-emerald-400" />
        </div>
        <p className="text-[10px] text-slate-600">Hold to speak — or type below</p>
      </div>
    </div>
  </div>
);

/* ═══════════════════ REVEAL WRAPPER ═══════════════════ */

const RevealSection: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => {
  const ref = useReveal();
  return <div ref={ref} className={`rel-reveal ${className}`}>{children}</div>;
};

/* ═══════════════════ SECTION NAV ═══════════════════ */

const SECTIONS = [
  { id: 'chat',     label: 'Expert Chat',    icon: MessageCircle, v: '' },
  { id: 'intel',   label: 'Intelligence',   icon: Brain,         v: '' },
  { id: 'strategy',label: 'Strategies',     icon: Library,       v: '' },
  { id: 'learning',label: 'Self-Learning',  icon: Lightbulb,     v: '' },
  { id: 'mcp',     label: 'MCP',            icon: Plug,          v: '' },
  { id: 'banker',  label: 'Banker',         icon: Landmark,      v: '' },
  { id: 'voice',   label: 'Voice & Avatar', icon: Volume2,       v: 'v3' },
  { id: 'rbac',    label: 'Permissions',    icon: Lock,          v: 'v4' },
  { id: 'stratea', label: 'Strategy EA',    icon: TrendingUp,    v: 'v5' },
  { id: 'scale',   label: 'Production Scale', icon: Server,      v: 'v6' },
  { id: 'platform',label: 'Platform & UX',  icon: Palette,       v: 'v7' },
  { id: 'markets', label: 'Global Markets', icon: Globe,         v: 'v8' },
  { id: 'b2b',       label: 'B2B & Compliance',   icon: Users,       v: 'v9' },
  { id: 'ipo',       label: 'IPO & Intelligence', icon: Rocket,      v: 'v10' },
  { id: 'notify',    label: 'Notifications',      icon: Bell,        v: 'v11' },
  { id: 'realtime',  label: 'Real-Time Data',     icon: Wifi,        v: 'v21' },
  { id: 'aiops',     label: 'AI Ops',             icon: Wrench,      v: 'v20' },
  { id: 'signals',   label: 'Strategy Signals',   icon: Layers,      v: 'v22' },
  { id: 'devops',    label: 'GitOps & Scale',     icon: GitBranch,   v: 'v25' },
  { id: 'platform2', label: 'B2B Platform',       icon: CreditCard,  v: 'v28' },
  { id: 'pricing',   label: 'Dynamic Pricing',    icon: DollarSign,  v: 'v29' },
] as const;

const SECTION_IDS = SECTIONS.map((s) => s.id);

/* ═══════════════════ MAIN PAGE ═══════════════════ */

const Releases: React.FC = () => {
  const navigate = useNavigate();
  const activeSection = useActiveSection(SECTION_IDS as unknown as string[]);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => { injectStyles(); }, []);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsVisible(true); }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const s1 = useCounter(37, statsVisible);
  const s2 = useCounter(210, statsVisible);
  const s3 = useCounter(13, statsVisible);
  const s4 = useCounter(19, statsVisible);
  const s5 = useCounter(73, statsVisible);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#000000] text-white selection:bg-teal-500/30 overflow-x-hidden">
      <SEO
        title="Release Notes"
        description="What's new in My Investments — latest feature releases, improvements, and bug fixes."
        canonical="https://my-investments.io/releases"
      />

      {/* ══ NAV ══ */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#000000]/80 backdrop-blur-2xl backdrop-saturate-150 border-b border-white/[0.04]">
        <div className="max-w-6xl mx-auto px-6 h-12 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-slate-500 hover:text-white transition-colors text-sm">
            <ArrowLeft size={14} />
            <span className="hidden sm:inline">Back</span>
          </button>
          <span className="text-sm font-semibold tracking-wide text-slate-300">MY INVESTMENTS</span>
          <button onClick={() => navigate('/register')} className="text-sm text-teal-400 hover:text-teal-300 font-medium transition-colors">
            Get Started
          </button>
        </div>
      </nav>

      {/* ══ HERO ══ */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
        <div className="absolute top-1/4 left-1/3 w-[700px] h-[700px] bg-teal-500/[0.07] rounded-full blur-[150px] rel-orb-1 pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/3 w-[500px] h-[500px] bg-indigo-500/[0.05] rounded-full blur-[130px] rel-orb-2 pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-6 text-center pt-12">
          <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white/[0.03] border border-white/[0.06] mb-10">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
            <span className="text-[13px] font-medium text-slate-400">v29.0</span>
            <span className="w-px h-3 bg-white/10" />
            <span className="text-[13px] text-slate-600">April 2026</span>
          </div>

          <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-[110px] font-black tracking-[-0.03em] leading-[0.9] mb-8">
            <span className="text-white block">Full-Stack AI</span>
            <span className="rel-gradient-text block">Trading Platform.</span>
          </h1>

          <p className="text-lg md:text-xl text-slate-500 max-w-xl mx-auto leading-relaxed mb-12">
            Expert analyst, voice banker, global markets across 73 exchanges, real-time WebSocket data, GitOps production infrastructure, B2B org management, and dynamic pricing — the complete ecosystem.
          </p>

          {/* Jump-to section chips */}
          <div className="flex flex-wrap justify-center gap-2">
            {SECTIONS.map((sec) => {
              const SIcon = sec.icon;
              return (
                <button
                  key={sec.id}
                  onClick={() => scrollTo(sec.id)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.08] hover:border-white/[0.12] transition-all text-sm text-slate-400 hover:text-white"
                >
                  <SIcon size={14} />
                  {sec.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══ STICKY SECTION NAV ══ */}
      <div className="sticky top-12 z-40 bg-[#000000]/80 backdrop-blur-2xl border-b border-white/[0.04]">
        <div className="max-w-6xl mx-auto px-6 flex items-center gap-1 overflow-x-auto py-2 scrollbar-none">
          {SECTIONS.map((sec) => {
            const SIcon = sec.icon;
            const vLabel = (sec as { v?: string }).v || '';
            const vColors: Record<string, string> = {
              v3:  'bg-emerald-500/15 text-emerald-400',
              v4:  'bg-violet-500/15 text-violet-400',
              v5:  'bg-amber-500/15 text-amber-400',
              v6:  'bg-sky-500/15 text-sky-400',
              v7:  'bg-rose-500/15 text-rose-400',
              v8:  'bg-cyan-500/15 text-cyan-400',
              v9:  'bg-orange-500/15 text-orange-400',
              v10: 'bg-pink-500/15 text-pink-400',
              v11: 'bg-indigo-500/15 text-indigo-400',
              v21: 'bg-amber-500/15 text-amber-400',
              v20: 'bg-rose-500/15 text-rose-300',
              v22: 'bg-violet-500/15 text-violet-300',
              v25: 'bg-slate-500/15 text-slate-300',
              v28: 'bg-orange-500/15 text-orange-300',
              v29: 'bg-teal-500/15 text-teal-300',
            };
            return (
              <React.Fragment key={sec.id}>
                {vLabel && <span className="w-px h-4 bg-white/[0.08] mx-1 flex-shrink-0" />}
                <button
                  onClick={() => scrollTo(sec.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                    activeSection === sec.id ? 'rel-nav-active' : 'text-slate-600 hover:text-slate-300'
                  }`}
                >
                  <SIcon size={12} />
                  {sec.label}
                  {vLabel && <span className={`ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold ${vColors[vLabel]}`}>{vLabel}</span>}
                </button>
              </React.Fragment>
            );
          })}
          <div className="flex-1" />
          <button onClick={() => scrollTo('plans')} className="text-xs text-slate-600 hover:text-slate-300 px-3 py-1.5 whitespace-nowrap transition-colors">Plans</button>
        </div>
      </div>

      {/* ══ STATS ══ */}
      <section ref={statsRef} className="py-16 border-b border-white/[0.04]">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-5 gap-8">
          {[
            { v: s1, l: 'Epics' },
            { v: s2, l: 'User Stories' },
            { v: s3, l: 'MCP Tools' },
            { v: s4, l: 'Themes' },
            { v: s5, l: 'Exchanges' },
          ].map((s) => (
            <div key={s.l} className="text-center">
              <p className="text-4xl md:text-5xl font-black tracking-tight text-white">{s.v}</p>
              <p className="text-xs text-slate-600 mt-2 font-medium">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════ EPIC SECTIONS ══════════════ */}

      {/* ── EPIC 1: EXPERT CHAT ── */}
      <section id="chat" className="py-24 md:py-32 scroll-mt-24 bg-gradient-to-b from-teal-500/[0.08] to-transparent">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-teal-400/70 mb-4">Expert Chat</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.05] mb-6">
                An AI that knows<br /><span className="rel-gradient-text">your trading.</span>
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                Floating chat assistant with specialist personas, domain knowledge, and real-time tool execution.
              </p>
              <div className="space-y-4">
                {[
                  { icon: MessageCircle, t: 'Floating Drawer', d: 'Persistent chat from any page with full-page mode' },
                  { icon: Zap, t: 'Streaming + Tool Pills', d: 'Watch tokens and tool calls happen in real-time' },
                  { icon: Brain, t: 'Multi-Persona System', d: 'Market Analyst, Investment Banker — each with distinct expertise' },
                ].map((f) => (
                  <div key={f.t} className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-lg bg-teal-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <f.icon size={14} className="text-teal-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{f.t}</p>
                      <p className="text-sm text-slate-500">{f.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <ChatMockup />
          </div>
        </RevealSection>
      </section>

      <div className="max-w-5xl mx-auto px-6"><div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" /></div>

      {/* ── EPIC 2: MARKET INTELLIGENCE ── */}
      <section id="intel" className="py-24 md:py-32 scroll-mt-24 bg-white">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <IntelMockup />
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-indigo-400/70 mb-4">Market Intelligence</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-gray-900 leading-[1.05] mb-6">
                Portfolio-aware.<br /><span className="rel-gradient-text">Context-driven.</span>
              </h2>
              <p className="text-lg text-gray-500 leading-relaxed mb-8">
                Ask about any trade and get structured analysis with your live positions, strategy rules, and real market data.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Eye, t: 'Portfolio Context', d: 'AI sees your live P&L, positions, and risk exposure' },
                  { icon: Shield, t: 'Risk Assessment', d: 'Drawdown limits, sizing, correlation, and sector exposure' },
                  { icon: Target, t: 'Smart Recommendations', d: 'Opportunities matching your strategy with entry/exit levels' },
                ].map((f) => (
                  <div key={f.t} className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-200/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <f.icon size={14} className="text-indigo-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{f.t}</p>
                      <p className="text-sm text-gray-500">{f.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </RevealSection>
      </section>

      <div className="max-w-5xl mx-auto px-6"><div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" /></div>

      {/* ── EPIC 3: STRATEGY EXTRACTION ── */}
      <section id="strategy" className="py-24 md:py-32 scroll-mt-24 bg-gradient-to-b from-violet-500/[0.08] to-transparent">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-violet-400/70 mb-4">Strategy Extraction</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.05] mb-6">
                Paste a URL.<br /><span className="rel-gradient-text">Get a strategy.</span>
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                YouTube video to scored trading strategy — fully automated. Download, transcribe, analyze, store.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Library, t: 'Strategy Library', d: 'Browse, search, compare and manage extracted strategies' },
                  { icon: BarChart3, t: 'Completeness Scoring', d: 'Entries, exits, risk rules and timeframes — scored 0-100' },
                  { icon: Zap, t: 'Auto Skill Promotion', d: 'Promote strategies to skill files for live Expert Chat advice' },
                ].map((f) => (
                  <div key={f.t} className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <f.icon size={14} className="text-violet-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{f.t}</p>
                      <p className="text-sm text-slate-500">{f.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <PipelineMockup />
          </div>
        </RevealSection>
      </section>

      <div className="max-w-5xl mx-auto px-6"><div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" /></div>

      {/* ── EPIC 4: SELF-LEARNING ── */}
      <section id="learning" className="py-24 md:py-32 scroll-mt-24 bg-white">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <LearningMockup />
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-amber-400/70 mb-4">Self-Learning</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-gray-900 leading-[1.05] mb-6">
                It remembers.<br /><span className="rel-gradient-text">It evolves.</span>
              </h2>
              <p className="text-lg text-gray-500 leading-relaxed mb-8">
                Correct the AI once and it learns. Knowledge stored at four levels, validated over time.
              </p>
              <div className="space-y-4">
                {[
                  { icon: MessageCircle, t: 'Correction Capture', d: 'Mid-conversation corrections are recorded and applied' },
                  { icon: BarChart3, t: 'Strategy Health', d: 'Win rate and drawdown tracked per strategy' },
                  { icon: Shield, t: 'Tiered Validation', d: 'Learnings start tentative, gain confidence over time' },
                ].map((f) => (
                  <div key={f.t} className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <f.icon size={14} className="text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{f.t}</p>
                      <p className="text-sm text-gray-500">{f.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </RevealSection>
      </section>

      <div className="max-w-5xl mx-auto px-6"><div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" /></div>

      {/* ── EPIC 5: MCP ── */}
      <section id="mcp" className="py-24 md:py-32 scroll-mt-24 bg-gradient-to-b from-cyan-500/[0.08] to-transparent">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-cyan-400/70 mb-4">MCP Integration</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.05] mb-6">
                Your tools,<br /><span className="rel-gradient-text">everywhere.</span>
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                13 trading tools exposed via Model Context Protocol. Connect from Claude Desktop, Cursor, or Claude Code.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Plug, t: 'Connect Any AI Tool', d: 'Works with Claude Desktop, Cursor, Windsurf, Claude Code, and more' },
                  { icon: Radio, t: '13 Tools Available', d: 'Market data, portfolio, trades, strategies, signals — all accessible via API' },
                  { icon: Shield, t: 'Secure by Default', d: 'API key authentication with per-key revocation and usage tracking' },
                ].map((f) => (
                  <div key={f.t} className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <f.icon size={14} className="text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{f.t}</p>
                      <p className="text-sm text-slate-500">{f.d}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <Link to="/mcp" className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600/20 hover:bg-cyan-600/30 border border-cyan-500/30 text-cyan-300 rounded-lg text-sm font-medium transition-colors">
                  Full Setup Guide →
                </Link>
              </div>
            </div>
            <MCPMockup />
          </div>
        </RevealSection>
      </section>

      <div className="max-w-5xl mx-auto px-6"><div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" /></div>

      {/* ── EPIC 6: INVESTMENT BANKER ── */}
      <section id="banker" className="py-24 md:py-32 scroll-mt-24 bg-white">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <BankerMockup />
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-rose-400/70 mb-4">Investment Banker</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-gray-900 leading-[1.05] mb-6">
                Your 24/7<br /><span className="rel-gradient-text">monitoring engine.</span>
              </h2>
              <p className="text-lg text-gray-500 leading-relaxed mb-8">
                Watches your positions, discovers opportunities, validates leads, and pushes alerts — running 24/7 in the background.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Eye, t: 'Position Oversight', d: 'P&L, ATR, S/R proximity — 3-tier alerts' },
                  { icon: Target, t: 'Opportunity Discovery', d: 'Scans global markets and scores opportunities by confidence' },
                  { icon: Bell, t: 'Multi-Channel Alerts', d: 'In-app, Expert Chat, and EA push notifications' },
                ].map((f) => (
                  <div key={f.t} className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-lg bg-rose-50 border border-rose-200/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <f.icon size={14} className="text-rose-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{f.t}</p>
                      <p className="text-sm text-gray-500">{f.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </RevealSection>
      </section>

      {/* ══ v3 DIVIDER ══ */}
      <section className="py-16 border-t border-white/[0.04]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white/[0.03] border border-white/[0.06]">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            <span className="text-[13px] font-medium text-emerald-400">v3.0</span>
            <span className="w-px h-3 bg-white/10" />
            <span className="text-[13px] text-slate-500">Interactive Banker Avatar</span>
          </div>
        </div>
      </section>

      {/* ── EPIC 7: VOICE & AVATAR ── */}
      <section id="voice" className="py-24 md:py-32 scroll-mt-24 bg-gradient-to-b from-emerald-500/[0.08] to-transparent">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-emerald-400/70 mb-4">Voice & Avatar</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.05] mb-6">
                Talk to your<br /><span className="rel-gradient-text">Banker.</span>
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                Voice conversations with a 2D animated character. Push-to-talk input, streaming voice responses, and an expressive lip-synced avatar that reacts to market conditions.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Volume2, t: 'Text-to-Speech', d: 'Banker alerts and chat responses spoken aloud in real-time with voice selection' },
                  { icon: Mic, t: 'Voice Input', d: 'Push-to-talk with browser STT — hands-free voice conversation loop' },
                  { icon: Sparkles, t: '2D Animated Avatar', d: 'Expressive character with mood states, lip sync, and pop-out overlay' },
                  { icon: Cpu, t: '3D Avatar with Lip Sync', d: 'Full 3D banker model with real-time phoneme-based lip sync and expression control' },
                ].map((f) => (
                  <div key={f.t} className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <f.icon size={14} className="text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{f.t}</p>
                      <p className="text-sm text-slate-500">{f.d}</p>
                    </div>
                  </div>
                ))}
              </div>
              {/* Expression states preview */}
              <div className="mt-8 flex flex-wrap gap-2">
                {['Idle', 'Thinking', 'Speaking', 'Confident', 'Concerned', 'Greeting'].map((state) => (
                  <span key={state} className="px-3 py-1.5 rounded-full text-[11px] font-medium bg-emerald-500/[0.06] text-emerald-400/80 border border-emerald-500/10">
                    {state}
                  </span>
                ))}
              </div>
            </div>
            <VoiceAvatarMockup />
          </div>
        </RevealSection>
      </section>

      <div className="max-w-5xl mx-auto px-6"><div className="h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" /></div>

      {/* ══ v4 DIVIDER ══ */}
      <section className="py-16 border-t border-white/[0.04]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white/[0.03] border border-white/[0.06]">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
            <span className="text-[13px] font-medium text-violet-400">v4.0</span>
            <span className="w-px h-3 bg-white/10" />
            <span className="text-[13px] text-slate-500">Platform RBAC & Permissions Engine</span>
          </div>
        </div>
      </section>

      {/* ── v4: RBAC & PERMISSIONS ── */}
      <section id="rbac" className="py-24 md:py-32 scroll-mt-24 bg-white">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-violet-400/70 mb-4">Roles & Permissions</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-gray-900 leading-[1.05] mb-6">
                Granular access<br /><span className="rel-gradient-text">for every role.</span>
              </h2>
              <p className="text-lg text-gray-500 leading-relaxed mb-8">
                Fine-grained access control for every feature. Roles, overrides, feature gates, and upgrade prompts — managed from the admin panel.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Lock, t: 'Role-Based Access Control', d: 'DB-backed roles with granular per-feature permission grants' },
                  { icon: Shield, t: 'Feature Gates & Upgrade Prompts', d: 'Users see contextual upgrade prompts when accessing gated features' },
                  { icon: Eye, t: 'Admin Role Management UI', d: 'Assign roles, set overrides, and audit permission changes from the admin panel' },
                ].map((f) => (
                  <div key={f.t} className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-lg bg-violet-50 border border-violet-200/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <f.icon size={14} className="text-violet-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{f.t}</p>
                      <p className="text-sm text-gray-500">{f.d}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-2">
                {['Admin', 'Premium', 'Pro', 'Free', 'Custom Override', 'Audit Log'].map((tag) => (
                  <span key={tag} className="px-3 py-1.5 rounded-full text-[11px] font-medium bg-violet-50 text-violet-500 border border-violet-200/60">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            {/* RBAC mockup */}
            <div className="rounded-2xl border border-gray-200 bg-gray-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                <Shield size={14} className="text-violet-400" />
                <span className="text-xs text-gray-500 font-medium">Role Management</span>
                <span className="ml-auto px-2 py-0.5 rounded text-[10px] bg-violet-50 text-violet-500 font-medium">Admin</span>
              </div>
              <div className="p-4 space-y-3">
                {[
                  { role: 'Premium', color: 'teal', perms: ['Investment Banker', 'MCP Integration', 'Unlimited AI', 'Priority Support'] },
                  { role: 'Pro', color: 'indigo', perms: ['Trading Analyst', 'Expert Chat', 'Strategy Library', 'Screener Export'] },
                  { role: 'Free', color: 'slate', perms: ['Market Data', 'Basic Charts', 'Watchlist', '5 Alerts'] },
                ].map((r) => (
                  <div key={r.role} className="rounded-xl border border-gray-200 bg-white p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`w-2 h-2 rounded-full bg-${r.color}-400`} />
                      <span className="text-sm font-semibold text-gray-900">{r.role}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {r.perms.map((p) => (
                        <span key={p} className="px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-500">{p}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </RevealSection>
      </section>

      <div className="max-w-5xl mx-auto px-6"><div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" /></div>

      {/* ══ v5 DIVIDER ══ */}
      <section className="py-16 border-t border-white/[0.04]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white/[0.03] border border-white/[0.06]">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            <span className="text-[13px] font-medium text-amber-400">v5.0</span>
            <span className="w-px h-3 bg-white/10" />
            <span className="text-[13px] text-slate-500">Strategy-Driven EA</span>
          </div>
        </div>
      </section>

      {/* ── v5: STRATEGY-DRIVEN EA ── */}
      <section id="stratea" className="py-24 md:py-32 scroll-mt-24 bg-gradient-to-b from-amber-500/[0.08] to-transparent">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Strategy EA mockup */}
            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
              <div className="px-4 py-3 border-b border-white/[0.04] flex items-center gap-2">
                <TrendingUp size={14} className="text-amber-400" />
                <span className="text-xs text-slate-500 font-medium">Active Strategy</span>
                <span className="ml-auto px-2 py-0.5 rounded text-[10px] bg-amber-500/10 text-amber-400 font-medium">Live</span>
              </div>
              <div className="p-4 space-y-3">
                {/* Strategy card */}
                <div className="rounded-xl border border-amber-500/10 bg-amber-500/[0.03] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-white">Zone Retest + MTF Trend</span>
                    <span className="px-2 py-0.5 rounded text-[10px] bg-amber-500/10 text-amber-400">User Strategy</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    {[['SL', '1.5× ATR'], ['TP', '2.5× ATR'], ['Conf', '≥ 70%']].map(([k, v]) => (
                      <div key={k} className="bg-white/[0.03] rounded-lg p-2">
                        <p className="text-[10px] text-slate-600 mb-0.5">{k}</p>
                        <p className="text-xs font-bold text-white">{v}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Eval profile */}
                <div className="rounded-xl border border-white/[0.04] bg-white/[0.02] p-3">
                  <p className="text-[10px] text-slate-600 mb-2 font-medium uppercase tracking-wider">Merged Eval Profile</p>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-600 w-24">Allowed Signals</span>
                      <div className="flex gap-1">
                        {['zone_retest', 'mtf_trend'].map((s) => (
                          <span key={s} className="px-1.5 py-0.5 rounded text-[9px] bg-teal-500/10 text-teal-400">{s}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-600 w-24">Sessions</span>
                      <div className="flex gap-1">
                        {['london', 'overlap'].map((s) => (
                          <span key={s} className="px-1.5 py-0.5 rounded text-[9px] bg-indigo-500/10 text-indigo-400">{s}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Signal gating status */}
                <div className="flex items-center gap-2 rounded-lg border border-green-500/10 bg-green-500/[0.03] px-3 py-2">
                  <CheckCircle2 size={12} className="text-green-400" />
                  <span className="text-xs text-slate-400">Signal passes gate — zone_retest · london · conf 74%</span>
                </div>
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-amber-400/70 mb-4">Strategy-Driven EA</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.05] mb-6">
                EA that trades<br /><span className="rel-gradient-text">your strategy.</span>
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                The EA downloads your active strategy parameters from the backend and uses them to gate signals, set SL/TP sizing, and filter by session — no manual config required.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Library, t: 'Strategy Library + Community Seeds', d: 'User strategies alongside community-contributed presets with SL/TP multipliers and eval profiles' },
                  { icon: Zap, t: 'Local Signal Gating', d: 'EA blocks off-strategy signals before sending to LLM — subtype and session filters from merged eval profile' },
                  { icon: TrendingUp, t: 'Strategy SL/TP Sizing', d: 'Confirmation response carries matched strategy name and ATR multipliers — EA overrides SL/TP automatically' },
                  { icon: Cpu, t: 'EA Integration Library', d: 'Shared library for pattern recognition, commodity trading, and future EA strategies — auto-syncs with the platform' },
                ].map((f) => (
                  <div key={f.t} className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <f.icon size={14} className="text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{f.t}</p>
                      <p className="text-sm text-slate-500">{f.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </RevealSection>
      </section>

      <div className="max-w-5xl mx-auto px-6"><div className="h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" /></div>

      {/* ══ v6 DIVIDER ══ */}
      <section className="py-16 border-t border-white/[0.04]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white/[0.03] border border-white/[0.06]">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
            <span className="text-[13px] font-medium text-sky-400">v6.0</span>
            <span className="w-px h-3 bg-white/10" />
            <span className="text-[13px] text-slate-500">Production Scale</span>
          </div>
        </div>
      </section>

      {/* ── v6: PRODUCTION SCALE ── */}
      <section id="scale" className="py-24 md:py-32 scroll-mt-24 bg-white">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Infrastructure mockup */}
            <div className="rounded-2xl border border-gray-200 bg-gray-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                <Activity size={14} className="text-sky-400" />
                <span className="text-xs text-gray-500 font-medium">Service Health Monitor</span>
                <span className="ml-auto px-2 py-0.5 rounded text-[10px] bg-green-50 text-green-500 font-medium">13/13 Healthy</span>
              </div>
              <div className="p-4 space-y-2">
                {[
                  { name: 'backend', replicas: '3×', status: 'healthy', color: 'text-green-500' },
                  { name: 'ea-sidecar', replicas: '2×', status: 'healthy', color: 'text-green-500' },
                  { name: 'ea-lb (nginx)', replicas: '1×', status: 'healthy', color: 'text-green-500' },
                  { name: 'celery-worker', replicas: '1×', status: 'healthy', color: 'text-green-500' },
                  { name: 'redis-streams', replicas: '1×', status: 'healthy', color: 'text-green-500' },
                  { name: 'pgbouncer', replicas: '1×', status: 'healthy', color: 'text-green-500' },
                ].map((svc) => (
                  <div key={svc.name} className="flex items-center gap-3 rounded-lg bg-white border border-gray-200 px-3 py-2">
                    <span className={`w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0`} />
                    <span className="text-xs text-gray-600 font-mono flex-1">{svc.name}</span>
                    <span className="text-[10px] text-gray-400">{svc.replicas}</span>
                    <span className={`text-[10px] font-medium ${svc.color}`}>{svc.status}</span>
                  </div>
                ))}
                <div className="flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 mt-3">
                  <Globe size={11} className="text-sky-500" />
                  <span className="text-xs text-gray-500">Load tested · <span className="text-sky-600 font-semibold">80,000 concurrent users</span></span>
                </div>
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-sky-400/70 mb-4">Production Scale</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-gray-900 leading-[1.05] mb-6">
                Built for the<br /><span className="rel-gradient-text">real world.</span>
              </h2>
              <p className="text-lg text-gray-500 leading-relaxed mb-8">
                Horizontally scaled, load tested to 80,000 concurrent users, with live config reload, Redis Streams event bus, and a real-time service health monitor.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Server, t: 'Multi-Pod Horizontal Scaling', d: 'nginx load balancer across 2 EA-sidecar replicas; 3 backend pods; Docker Compose orchestration with health checks' },
                  { icon: Database, t: 'DB-Backed Live Config', d: 'All settings persist via app_settings table and live-reload to all pods via Redis pub/sub — no restart required' },
                  { icon: Radio, t: 'Redis Streams Event Bus', d: 'Consumer group architecture with pending message monitor, dead-letter tracking, and lag dashboards' },
                  { icon: Activity, t: 'Service Health Monitor', d: '13-service observability dashboard at /monitor — postgres, redis, flower, expo push, EODHD MCP and more' },
                  { icon: Globe, t: 'Live Web Search', d: 'Real-time news and market search wired into Banker discovery and Expert Chat' },
                ].map((f) => (
                  <div key={f.t} className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-lg bg-sky-50 border border-sky-200/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <f.icon size={14} className="text-sky-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{f.t}</p>
                      <p className="text-sm text-gray-500">{f.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </RevealSection>
      </section>

      <div className="max-w-5xl mx-auto px-6"><div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" /></div>

      {/* ══ v7 DIVIDER ══ */}
      <section className="py-16 border-t border-white/[0.04]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white/[0.03] border border-white/[0.06]">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
            <span className="text-[13px] font-medium text-rose-400">v7.0</span>
            <span className="w-px h-3 bg-white/10" />
            <span className="text-[13px] text-slate-500">Platform & UX</span>
          </div>
        </div>
      </section>

      {/* ── v7: PLATFORM & UX ── */}
      <section id="platform" className="py-24 md:py-32 scroll-mt-24 bg-gradient-to-b from-rose-500/[0.08] to-transparent">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-rose-400/70 mb-4">Platform & UX</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.05] mb-6">
                Your dashboard,<br /><span className="rel-gradient-text">your way.</span>
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                11 themes with live preview, branch appointment booking, a searchable knowledge hub, role-based user management, and mobile push notifications.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Palette, t: '11 Themes + Live Preview', d: 'Dark, Snow, Rose, Dracula, Nord, Ocean, Solarized, Zinc, Midnight, Terminal, Warm — with 5 accent colors, font sizes, chart schemes, and animation controls. Previews instantly on click.' },
                  { icon: Calendar, t: 'Branch Appointment Booking', d: 'Per-branch availability scheduling, client-facing booking portal, and admin management panel' },
                  { icon: BookOpen, t: 'Knowledge Hub', d: 'Searchable articles, glossary, video library, and strategy guides — accessible from any page' },
                  { icon: Users, t: 'User Management & RBAC', d: 'Granular role and permission system with admin UI for user suspension, role assignment, and feature gating' },
                  { icon: Smartphone, t: 'Mobile Push Notifications', d: 'Expo-powered push delivery to iOS and Android — wired into Banker alerts, price alerts, and trade confirmations' },
                  { icon: Monitor, t: '3D Banker Avatar', d: 'WebGL animated avatar with ARKit blendshapes, lip sync via Web Audio API, and dual-persona switching' },
                ].map((f) => (
                  <div key={f.t} className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-lg bg-rose-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <f.icon size={14} className="text-rose-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{f.t}</p>
                      <p className="text-sm text-slate-500">{f.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Theme picker mockup */}
            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
              <div className="px-4 py-3 border-b border-white/[0.04] flex items-center gap-2">
                <Palette size={14} className="text-rose-400" />
                <span className="text-xs text-slate-500 font-medium">Display Settings</span>
                <span className="ml-auto px-2 py-0.5 rounded text-[10px] bg-rose-500/10 text-rose-400 font-medium">Live Preview</span>
              </div>
              <div className="p-4 space-y-4">
                {/* Theme grid */}
                <div>
                  <p className="text-[10px] text-slate-600 mb-2 font-medium uppercase tracking-wider">Theme</p>
                  <div className="grid grid-cols-4 gap-1.5">
                    {[
                      { label: 'Dark',     bg: '#0f172a', sb: '#1e293b', ac: '#2dd4bf' },
                      { label: 'Snow',     bg: '#f0f4f8', sb: '#ffffff', ac: '#2dd4bf', active: true },
                      { label: 'Dracula',  bg: '#282a36', sb: '#21222c', ac: '#bd93f9' },
                      { label: 'Nord',     bg: '#2e3440', sb: '#3b4252', ac: '#88c0d0' },
                      { label: 'Ocean',    bg: '#0a192f', sb: '#112240', ac: '#64ffda' },
                      { label: 'Rose',     bg: '#fff1f2', sb: '#ffffff', ac: '#f43f5e' },
                      { label: 'Solarized',bg: '#002b36', sb: '#073642', ac: '#268bd2' },
                      { label: 'Warm',     bg: '#12100e', sb: '#1c1814', ac: '#f59e0b' },
                    ].map((t) => (
                      <div key={t.label} className={`flex flex-col items-center gap-1 p-1.5 rounded-lg border transition-all ${t.active ? 'border-rose-400/50 bg-rose-500/10' : 'border-white/[0.04]'}`}>
                        <div className="w-full h-7 rounded overflow-hidden flex" style={{ backgroundColor: t.bg }}>
                          <div className="w-2.5 h-full" style={{ backgroundColor: t.sb }} />
                          <div className="flex-1 flex flex-col justify-end p-0.5 gap-0.5">
                            <div className="h-0.5 rounded-full opacity-70" style={{ backgroundColor: t.ac, width: '70%' }} />
                            <div className="h-0.5 rounded-full opacity-30 bg-white w-full" />
                          </div>
                        </div>
                        <span className="text-[8px] text-slate-600">{t.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Accent color row */}
                <div>
                  <p className="text-[10px] text-slate-600 mb-2 font-medium uppercase tracking-wider">Accent</p>
                  <div className="flex gap-2">
                    {['#2dd4bf','#60a5fa','#fbbf24','#4ade80','#a78bfa'].map((c, i) => (
                      <div key={c} className={`w-5 h-5 rounded-full border-2 ${i === 0 ? 'border-white' : 'border-transparent'}`} style={{ backgroundColor: c }} />
                    ))}
                  </div>
                </div>
                {/* Font size buttons */}
                <div>
                  <p className="text-[10px] text-slate-600 mb-2 font-medium uppercase tracking-wider">Font Size</p>
                  <div className="flex gap-1.5">
                    {['Small','Medium','Large'].map((s, i) => (
                      <div key={s} className={`flex-1 text-center py-1 rounded text-[9px] border ${i === 1 ? 'border-teal-400/50 bg-teal-500/10 text-teal-400' : 'border-white/[0.06] text-slate-600'}`}>{s}</div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 rounded-lg border border-rose-500/10 bg-rose-500/[0.03] px-3 py-2">
                  <CheckCircle2 size={11} className="text-rose-400" />
                  <span className="text-xs text-slate-400">Theme previews instantly — no Save needed</span>
                </div>
              </div>
            </div>
          </div>
        </RevealSection>
      </section>

      <div className="max-w-5xl mx-auto px-6"><div className="h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" /></div>

      {/* ══ v8 DIVIDER ══ */}
      <section className="py-16 border-t border-white/[0.04]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white/[0.03] border border-white/[0.06]">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
            <span className="text-[13px] font-medium text-cyan-400">v8.0</span>
            <span className="w-px h-3 bg-white/10" />
            <span className="text-[13px] text-slate-500">Global Markets & Banker Intelligence</span>
          </div>
        </div>
      </section>

      {/* ── v8: GLOBAL MARKETS & BANKER INTELLIGENCE ── */}
      <section id="markets" className="py-24 md:py-32 scroll-mt-24 bg-white">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-cyan-400/70 mb-4">Global Markets & Banker Intelligence</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-gray-900 leading-[1.05] mb-6">
                Markets go global.<br /><span className="rel-gradient-text">Banker gets smarter.</span>
              </h2>
              <p className="text-lg text-gray-500 leading-relaxed mb-8">
                Four live exchange tabs, a per-user heatmap picker, banker leads that actively seek novel assets, 7-day cooldown deduplication, and rich notification embeds.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Globe,    t: 'US Top 50',                   d: 'S&P 500 leaders with real-time quotes, bulk-fetched and sorted by market cap — always the most-watched names.' },
                  { icon: Globe,    t: 'London LSE Tab',              d: 'FTSE 100 Top 50 with live prices, sector tags, and change indicators — full UK market coverage.' },
                  { icon: Globe,    t: 'Asia Tab',                    d: 'Tokyo (Nikkei 100), Hong Kong (Hang Seng), Shanghai (SSE 50), and ASX top 100 in one unified view.' },
                  { icon: BarChart3,t: 'Per-User Heatmap Picker',     d: 'Each user chooses up to 3 exchanges for their market heatmap — preference saved per account.' },
                  { icon: Landmark, t: 'Banker Lead Deduplication',   d: '7-day cooldown per symbol prevents re-alerting on the same asset. Configurable from admin settings.' },
                  { icon: Landmark, t: 'Novelty-Seeking Discovery',   d: 'Recent leads are injected into the Analyst and Banker LLM prompts so engines actively seek new, non-redundant opportunities.' },
                  { icon: Bell,     t: 'Rich Discord & Teams Embeds', d: 'Banker lead notifications now include exchange, confidence score, strategy rationale, and colour-coded urgency in Discord and Teams.' },
                ].map((f) => (
                  <div key={f.t} className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-lg bg-cyan-50 border border-cyan-200/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <f.icon size={14} className="text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{f.t}</p>
                      <p className="text-sm text-gray-500">{f.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Markets mockup */}
            <div className="rounded-2xl border border-gray-200 bg-gray-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                <Globe size={14} className="text-cyan-400" />
                <span className="text-xs text-gray-500 font-medium">Markets</span>
                <div className="ml-auto flex gap-1">
                  {['JSE', 'US', 'London', 'Asia'].map((tab, i) => (
                    <span key={tab} className={`px-2 py-0.5 rounded text-[10px] font-medium ${i === 2 ? 'bg-cyan-50 text-cyan-500' : 'text-gray-400'}`}>{tab}</span>
                  ))}
                </div>
              </div>
              <div className="p-4 space-y-2">
                {[
                  { sym: 'HSBA', name: 'HSBC Holdings',    chg: '+1.34%', pos: true },
                  { sym: 'BP',   name: 'BP plc',           chg: '-0.72%', pos: false },
                  { sym: 'RIO',  name: 'Rio Tinto',        chg: '+2.18%', pos: true },
                  { sym: 'AZN',  name: 'AstraZeneca',      chg: '+0.91%', pos: true },
                  { sym: 'SHEL', name: 'Shell plc',        chg: '-0.44%', pos: false },
                  { sym: 'ULVR', name: 'Unilever',         chg: '+0.63%', pos: true },
                ].map((row) => (
                  <div key={row.sym} className="flex items-center justify-between py-1.5 border-b border-gray-200 last:border-0">
                    <div>
                      <p className="text-xs font-semibold text-gray-900">{row.sym}</p>
                      <p className="text-[10px] text-gray-400">{row.name}</p>
                    </div>
                    <span className={`text-xs font-medium ${row.pos ? 'text-emerald-500' : 'text-red-500'}`}>{row.chg}</span>
                  </div>
                ))}
                <div className="flex items-center gap-2 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2 mt-2">
                  <CheckCircle2 size={11} className="text-cyan-500" />
                  <span className="text-xs text-gray-500">Heatmap exchange picker — up to 3 per user</span>
                </div>
              </div>
            </div>
          </div>
        </RevealSection>
      </section>

      <div className="max-w-5xl mx-auto px-6"><div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" /></div>

      {/* ══ v9 DIVIDER ══ */}
      <section className="py-16 border-t border-white/[0.04]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white/[0.03] border border-white/[0.06]">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
            <span className="text-[13px] font-medium text-violet-400">v9.0</span>
            <span className="w-px h-3 bg-white/10" />
            <span className="text-[13px] text-slate-500">B2B Platform, Compliance & Triple Confirmation</span>
          </div>
        </div>
      </section>

      {/* ── v9: B2B & COMPLIANCE ── */}
      <section id="b2b" className="py-24 md:py-32 scroll-mt-24 bg-gradient-to-b from-violet-500/[0.08] to-transparent">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-violet-400/70 mb-4">B2B Platform, Compliance & Triple Confirmation</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.05] mb-6">
                Enterprise-ready.<br /><span className="rel-gradient-text">Regulation-first.</span>
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                Full B2B org model with white-labelling, custom domains, compliance controls, KYC, and a triple-confirmation signal system layering Banker leads, watchlist scans, and EA confirmations.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Users,      t: 'B2B Organisation Model',         d: 'Multi-role orgs (admin, FM, lead analyst, DevOps, developer, member) with pricing agreements, FM invoice approval, and entitlement overrides per org.' },
                  { icon: Shield,     t: 'FAIS Compliance Layer',           d: 'Platform-wide FAIS disclaimer modal (scroll-to-confirm), persistent footer on all signal screens, strategy approval disclaimers, and a public FAQ with compliance section.' },
                  { icon: Lock,       t: 'KYC Document Management',        d: 'GCS-backed document upload (company reg, director IDs, proof of address, beneficial owner), admin review workflow, and broker-access gate.' },
                  { icon: Palette,    t: 'White-Label Branding',           d: 'GCS staging/publish workflow for logos and brand colours. CSS variable injection at runtime — no redeployment. Org branding delivered in login response.' },
                  { icon: Globe,      t: 'Custom Domains + SSL',           d: 'Cloudflare SSL for SaaS integration. Orgs map their subdomain via CNAME; nginx resolves Host header to org context; auto-org-join on custom domain registration.' },
                  { icon: Activity,   t: 'Prometheus Org-Scoped Metrics',  d: 'org_id label on every metric. LLM cost counters, banker cycle histograms, watchlist scan gauges. Pre-built Grafana dashboards: platform overview, per-org detail, LLM cost breakdown.' },
                  { icon: Target,     t: 'Triple Confirmation Signals',    d: 'Three independent AI engines must agree before a high-confidence signal is surfaced. Convergence badges highlight when multiple layers align on the same opportunity.' },
                  { icon: Eye,        t: 'Signal Action Policy',           d: 'Org admins set Open / Notify-Only / Gated policy. Gated mode holds signals in a Lead Analyst review queue before members see them — full audit trail.' },
                ].map((f) => (
                  <div key={f.t} className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <f.icon size={14} className="text-violet-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{f.t}</p>
                      <p className="text-sm text-slate-500">{f.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* B2B / Compliance mockup */}
            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
              <div className="px-4 py-3 border-b border-white/[0.04] flex items-center gap-2">
                <Users size={14} className="text-violet-400" />
                <span className="text-xs text-slate-500 font-medium">Organisation — Signal Queue</span>
                <span className="ml-auto px-2 py-0.5 rounded text-[10px] bg-amber-500/10 text-amber-400 font-medium">3 pending</span>
              </div>
              <div className="p-4 space-y-3">
                {[
                  { sym: 'NPN.JSE',  conf: 87, type: 'BUY',  conv: true  },
                  { sym: 'AAPL.US',  conf: 79, type: 'BUY',  conv: false },
                  { sym: 'GOLD.US',  conf: 91, type: 'SELL', conv: true  },
                ].map((row) => (
                  <div key={row.sym} className="rounded-xl border border-white/[0.04] bg-white/[0.02] p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <code className="text-xs font-bold text-white">{row.sym}</code>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${row.type === 'BUY' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>{row.type}</span>
                        {row.conv && <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-violet-500/10 text-violet-400 border border-violet-500/20">2/3 Converged</span>}
                      </div>
                      <span className="text-xs text-slate-500">{row.conf}%</span>
                    </div>
                    <div className="flex gap-2">
                      <button className="flex-1 py-1 rounded-lg text-[10px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/10">Approve</button>
                      <button className="flex-1 py-1 rounded-lg text-[10px] font-medium bg-red-500/10 text-red-400 border border-red-500/10">Reject</button>
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-2 rounded-lg border border-violet-500/10 bg-violet-500/[0.03] px-3 py-2">
                  <Shield size={11} className="text-violet-400" />
                  <span className="text-xs text-slate-400">Gated policy · Lead Analyst review required</span>
                </div>
              </div>
            </div>
          </div>
        </RevealSection>
      </section>

      {/* ══ v10 DIVIDER ══ */}
      <section className="py-6 border-t border-white/[0.04]">
        <div className="max-w-6xl mx-auto px-6 flex items-center gap-3">
          <Rocket size={14} className="text-teal-400" />
          <div>
            <span className="text-[13px] font-medium text-teal-400">v10.0</span>
            <span className="mx-2 text-slate-700">·</span>
            <span className="text-[13px] text-slate-500">IPO Calendar, Market Outlook & Geopolitical Intelligence</span>
          </div>
        </div>
      </section>

      {/* ── v10: IPO & INTELLIGENCE ── */}
      <section id="ipo" className="py-24 md:py-32 scroll-mt-24 bg-white">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-teal-400/70 mb-4">IPO Calendar, Market Outlook & Geopolitical Intelligence</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-gray-900 leading-[1.05] mb-6">
                The world is<br /><span className="rel-gradient-text">your context.</span>
              </h2>
              <p className="text-lg text-gray-500 leading-relaxed mb-8">
                IPO and crypto launch discovery from multiple data sources. A macro market outlook that feeds into every AI decision.
                The Banker and news predictions now know what's happening in the world — tariffs, central bank shifts, geopolitical risk — before making recommendations.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Rocket,     t: 'IPO & Crypto Launch Calendar',    d: 'Stock IPOs and crypto launches aggregated from multiple data sources into a single view. Filter by exchange, sector, chain, or date. Watch queue with auto-watchlist on listing.' },
                  { icon: Newspaper,  t: 'Market Outlook (every 4h)',       d: 'Live web search + AI synthesis produces risk sentiment, regional outlook (US/JSE/LSE/EU/Asia), key events, and a full narrative briefing.' },
                  { icon: Globe,      t: 'Geopolitical Awareness',          d: 'The Banker receives the Market Outlook as first context before evaluating leads. No more recommending cyclicals during risk-off. IPO evaluation also macro-aware.' },
                  { icon: Brain,      t: 'Enriched News Predictions',       d: 'Forex directional bias predictions now incorporate world news context from the Market Outlook — not just technical indicators and scheduled events.' },
                  { icon: Target,     t: 'On-Chain Launch Detection',       d: 'Detects new tokens the moment they launch across 80+ blockchains. Quality filters automatically exclude low-liquidity and suspicious tokens.' },
                  { icon: Eye,        t: 'Watch Queue & Listing Detection', d: 'Bookmark upcoming IPOs. Get notified when they list. Auto-added to your watchlist. Status tracking: awaiting → listed → stale.' },
                ].map((f) => (
                  <div key={f.t} className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-lg bg-teal-50 border border-teal-200/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <f.icon size={14} className="text-teal-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{f.t}</p>
                      <p className="text-sm text-gray-500">{f.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Market Outlook mockup */}
            <div className="rounded-2xl border border-gray-200 bg-gray-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                <Newspaper size={14} className="text-teal-400" />
                <span className="text-xs text-gray-500 font-medium">Market Outlook</span>
                <span className="ml-auto text-[10px] text-gray-400">Updated 2h ago</span>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-red-50 text-red-500">risk-off</span>
                  <span className="text-xs text-gray-900 font-medium">US-China tariff escalation; safe havens rally</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { r: 'US', o: 'bearish', c: 'text-red-500 bg-red-50' },
                    { r: 'JSE', o: 'neutral', c: 'text-gray-500 bg-gray-200' },
                    { r: 'LSE', o: 'bearish', c: 'text-red-500 bg-red-50' },
                    { r: 'EU', o: 'neutral', c: 'text-gray-500 bg-gray-200' },
                    { r: 'Asia', o: 'bearish', c: 'text-red-500 bg-red-50' },
                  ].map((chip) => (
                    <span key={chip.r} className={`px-2 py-0.5 rounded text-[10px] ${chip.c}`}>{chip.r}: {chip.o}</span>
                  ))}
                </div>
                <div className="space-y-2 pt-2 border-t border-gray-200">
                  {[
                    { dot: 'bg-red-400', text: 'US tariffs on Chinese tech exports expanded — semiconductor stocks down 4%' },
                    { dot: 'bg-amber-400', text: 'ECB holds rates steady, dovish forward guidance' },
                    { dot: 'bg-red-400', text: 'Gold breaks $2,400 on safe-haven demand' },
                  ].map((evt, i) => (
                    <div key={i} className="flex items-start gap-2 text-[11px]">
                      <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${evt.dot}`} />
                      <span className="text-gray-500">{evt.text}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <p className="text-[10px] text-gray-400">This outlook is injected into the Banker and forex predictions as macro context.</p>
                </div>
              </div>
            </div>
          </div>
        </RevealSection>
      </section>

      {/* ══ NOTIFICATIONS & WEBHOOKS (v11–v13) ══ */}
      <section id="notify" className="py-24 md:py-32 scroll-mt-24 border-t border-white/[0.04]">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-indigo-400/70 mb-4">v11 — v13</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.05] mb-6">
                Push notifications.<br /><span className="rel-gradient-text">At any scale.</span>
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                Real-time push notifications via Expo, Redis Streams event bus, and webhook alerts to Teams and Discord — all tested to 80,000 concurrent users.
              </p>
              <div className="space-y-3">
                {[
                  ['Expo Push Notification service — iOS, Android, web'],
                  ['Redis Streams event bus — banker leads, price alerts, trade closures'],
                  ['Teams & Discord webhook alerts with admin UI config'],
                  ['Push worker horizontal scaling + pgbouncer pool expansion'],
                  ['Load tested to 80k concurrent users with NFR baseline'],
                  ['Support ticket webhook notifications'],
                  ['Onboarding preferences schema — persisted per user'],
                ].map(([text], i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={15} className="text-indigo-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">{text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-900/60 border border-white/[0.06] rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Push Events</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-indigo-500/10 text-indigo-400 border border-indigo-500/10">Redis Streams</span>
              </div>
              {[
                { icon: '💼', label: 'banker-lead', desc: 'New opportunity: NPN +4.2% breakout', color: 'text-teal-400' },
                { icon: '🔔', label: 'price-alert', desc: 'GOLD crossed $2,400 resistance', color: 'text-amber-400' },
                { icon: '✅', label: 'trade-closed', desc: 'EUR/USD +1.8R — target hit', color: 'text-green-400' },
                { icon: '📢', label: 'system-announce', desc: 'Market opens in 15 minutes', color: 'text-slate-400' },
              ].map((ev) => (
                <div key={ev.label} className="flex items-center gap-3 bg-white/[0.02] rounded-lg px-4 py-3 border border-white/[0.04]">
                  <span className="text-lg">{ev.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-mono font-medium ${ev.color}`}>{ev.label}</p>
                    <p className="text-xs text-slate-500 truncate">{ev.desc}</p>
                  </div>
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse flex-shrink-0" />
                </div>
              ))}
              <div className="pt-2 border-t border-white/[0.04] flex items-center justify-between text-[11px] text-slate-600">
                <span>4 active event streams</span>
                <span>80k users · &lt;200ms p99</span>
              </div>
            </div>
          </div>
        </RevealSection>
      </section>

      {/* ══ REAL-TIME DATA & WEBSOCKET (v21) ══ */}
      <section id="realtime" className="py-24 md:py-32 scroll-mt-24 border-t border-white/[0.04]">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="order-2 lg:order-1 bg-slate-900/60 border border-white/[0.06] rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Live Feed</span>
                <span className="flex items-center gap-1.5 text-[10px] text-green-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />WebSocket
                </span>
              </div>
              {[
                { sym: 'NPN.JSE', px: '5,430.00', chg: '+1.24%', up: true },
                { sym: 'AAPL',   px: '189.42',   chg: '+0.38%', up: true },
                { sym: 'EUR/USD',px: '1.0847',   chg: '-0.12%', up: false },
                { sym: 'GOLD',   px: '2,401.15', chg: '+0.87%', up: true },
              ].map((row) => (
                <div key={row.sym} className="flex items-center justify-between bg-white/[0.02] rounded-lg px-4 py-2.5 border border-white/[0.04]">
                  <span className="text-sm font-mono font-medium text-white">{row.sym}</span>
                  <span className="text-sm text-slate-300">{row.px}</span>
                  <span className={`text-xs font-semibold ${row.up ? 'text-green-400' : 'text-red-400'}`}>{row.chg}</span>
                </div>
              ))}
              <div className="pt-2 border-t border-white/[0.04] grid grid-cols-2 gap-3">
                {[['RSI(14)', '62.4'], ['MACD', '+0.034'], ['BB Width', '2.1%'], ['ATR(14)', '18.3']].map(([lbl, val]) => (
                  <div key={lbl} className="bg-white/[0.02] rounded px-3 py-2 flex items-center justify-between">
                    <span className="text-[10px] text-slate-600">{lbl}</span>
                    <span className="text-xs text-amber-400 font-mono">{val}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-sm font-semibold tracking-widest uppercase text-amber-400/70 mb-4">v21</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.05] mb-6">
                Real-time everywhere.<br /><span className="rel-gradient-text">Zero polling.</span>
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                WebSocket fanout hub delivers live prices and technical indicators to every connected client simultaneously. Shared indicator cache means one calculation serves thousands of users.
              </p>
              <div className="space-y-3">
                {[
                  ['WebSocket fanout hub — broadcast to all connected clients'],
                  ['Technical indicators shared cache — RSI, MACD, Bollinger, ATR'],
                  ['JSE intraday bars from IRESS feed — real OHLCV, not delayed'],
                  ['Market hours scheduler — JSE, US, LSE, XETRA, AU, TO aware'],
                  ['JSE ETFs and indices from JSE-All list'],
                  ['US ETF expansion — 500+ instruments available'],
                  ['On-demand screener — strategy evaluation per request'],
                  ['iOS, Android, React Native live market WS indicators'],
                ].map(([text], i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={15} className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </RevealSection>
      </section>

      {/* ══ AI OPS & SELF-HEALING (v20) ══ */}
      <section id="aiops" className="py-24 md:py-32 scroll-mt-24 border-t border-white/[0.04]">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-rose-400/70 mb-4">v20</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.05] mb-6">
                AI that fixes<br /><span className="rel-gradient-text">its own bugs.</span>
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                The Exception Fix Agent monitors production errors in real time and automatically proposes fixes. Repeat exceptions trigger Discord alerts. All exceptions are stored, searchable, and auto-purged after 30 days.
              </p>
              <div className="space-y-3">
                {[
                  ['Exception store — captures all unhandled errors with full stack trace'],
                  ['Exception Fix Agent — LLM proposes fix for recurring errors'],
                  ['Admin exceptions UI — searchable, filterable error dashboard'],
                  ['Repeat exception Discord alerts — fires after 3+ occurrences'],
                  ['Auto-purge job — cleans exceptions older than 30 days'],
                  ['Internal task health endpoint + monitor panel'],
                  ['Watchdog Discord alerts — fires when task is overdue'],
                  ['Metrics instrumentation + Grafana dashboard'],
                ].map(([text], i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={15} className="text-rose-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">{text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-900/60 border border-white/[0.06] rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Exception Feed</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-rose-500/10 text-rose-400 border border-rose-500/10">Auto-Fix Agent</span>
              </div>
              {[
                { sev: 'HIGH',   msg: 'KeyError: strategy_id in signals_generator', cnt: 3, fixed: true },
                { sev: 'MEDIUM', msg: 'EODHD 500 on BHP.JSE — retrying', cnt: 7, fixed: false },
                { sev: 'LOW',    msg: 'pandas ChainedAssignment warning', cnt: 12, fixed: true },
              ].map((ex) => (
                <div key={ex.msg} className="bg-white/[0.02] rounded-lg px-4 py-3 border border-white/[0.04] space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${ex.sev === 'HIGH' ? 'bg-red-500/15 text-red-400' : ex.sev === 'MEDIUM' ? 'bg-amber-500/15 text-amber-400' : 'bg-slate-500/15 text-slate-400'}`}>{ex.sev}</span>
                    <span className="text-[10px] text-slate-500">{ex.cnt}× occurrences</span>
                    {ex.fixed && <span className="ml-auto text-[10px] text-green-400 flex items-center gap-1"><CheckCircle2 size={9} />Fix proposed</span>}
                  </div>
                  <p className="text-xs font-mono text-slate-400 truncate">{ex.msg}</p>
                </div>
              ))}
              <div className="pt-2 border-t border-white/[0.04] text-[11px] text-slate-600">2 fixes automatically proposed · 1 pending review</div>
            </div>
          </div>
        </RevealSection>
      </section>

      {/* ══ STRATEGY ENGINE v3 & SIGNALS (v22) ══ */}
      <section id="signals" className="py-24 md:py-32 scroll-mt-24 border-t border-white/[0.04]">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="bg-slate-900/60 border border-white/[0.06] rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Strategy Signals</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-violet-500/10 text-violet-400 border border-violet-500/10">v3 Engine</span>
              </div>
              {[
                { sym: 'MTN.JSE', sig: 'BREAKOUT', str: 'Zone Retest v3', conf: 88 },
                { sym: 'BHP.JSE', sig: 'TREND',    str: 'MTF Momentum',    conf: 74 },
                { sym: 'NPN.JSE', sig: 'REVERSAL', str: 'S/R Confluence',  conf: 91 },
              ].map((row) => (
                <div key={row.sym} className="bg-white/[0.02] rounded-lg px-4 py-3 border border-white/[0.04]">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-mono font-semibold text-white">{row.sym}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${row.sig === 'BREAKOUT' ? 'bg-teal-500/15 text-teal-400' : row.sig === 'TREND' ? 'bg-blue-500/15 text-blue-400' : 'bg-amber-500/15 text-amber-400'}`}>{row.sig}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-500">{row.str}</span>
                    <div className="flex-1 h-1 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-violet-500 to-violet-400 rounded-full" style={{ width: `${row.conf}%` }} />
                    </div>
                    <span className="text-[11px] text-violet-400 font-mono">{row.conf}%</span>
                  </div>
                </div>
              ))}
              <div className="pt-2 border-t border-white/[0.04] text-[11px] text-slate-600">Signals persisted to DB · visible in portfolio + screener</div>
            </div>
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-violet-400/70 mb-4">v22</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.05] mb-6">
                Strategy engine<br /><span className="rel-gradient-text">version 3.</span>
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                Rebuilt signal generation pipeline with price action analysis, IRESS real OHLCV ingestion, version-controlled strategy configs, and live signals visible across screener, portfolio, and mobile.
              </p>
              <div className="space-y-3">
                {[
                  ['Strategy parameters schema v3 — versioned, RBAC-controlled'],
                  ['Price action analysis service — S/R detection, breakout scoring'],
                  ['IRESS feed 2873 — real OHLCV ingestion replacing mock data'],
                  ['Screener strategy columns — toggle signal view per strategy'],
                  ['Dashboard personalised trading signals widget'],
                  ['Portfolio inline strategy label — see signal per position'],
                  ['Strategy-aware screener endpoints — filter by signal type'],
                  ['Mobile strategy signals — React Native + Android'],
                ].map(([text], i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={15} className="text-violet-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </RevealSection>
      </section>

      {/* ══ GITOPS & PRODUCTION SCALE (v25–v26) ══ */}
      <section id="devops" className="py-24 md:py-32 scroll-mt-24 border-t border-white/[0.04]">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-slate-400/70 mb-4">v25 — v26</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.05] mb-6">
                Production-grade<br /><span className="rel-gradient-text">GitOps pipeline.</span>
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                Full migration from Docker Compose to Kubernetes on k3s with ArgoCD GitOps. Every push to main automatically deploys to production. Blue-green deployments with zero-downtime rollouts.
              </p>
              <div className="space-y-3">
                {[
                  ['k3s Kubernetes cluster — all services running as k8s deployments'],
                  ['ArgoCD GitOps — auto-deploys on every main branch push'],
                  ['Blue-green deployments — zero-downtime rollouts'],
                  ['GitHub webhook → ArgoCD for instant sync (no 3-min polling)'],
                  ['Release-triggered PROD deploy pipeline — tag to ship'],
                  ['k8s resource limits for all MCP pods — memory controlled'],
                  ['Redis AOF persistence — no data loss on pod restart'],
                  ['Finance-core private wheel — 9 protected modules via Cython'],
                ].map(([text], i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={15} className="text-slate-300 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">{text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-900/60 border border-white/[0.06] rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Deploy Pipeline</span>
                <span className="flex items-center gap-1.5 text-[10px] text-green-400"><span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />ArgoCD Synced</span>
              </div>
              {[
                { step: 'git push → main',            status: 'done',    time: '0s' },
                { step: 'CI build + tests',           status: 'done',    time: '2m' },
                { step: 'Docker image → GHCR',        status: 'done',    time: '3m' },
                { step: 'kustomization.yaml updated', status: 'done',    time: '3m' },
                { step: 'ArgoCD detects change',      status: 'done',    time: '3m' },
                { step: 'k8s rolling update',         status: 'active',  time: '4m' },
                { step: 'Health check passes',        status: 'pending', time: '—'  },
              ].map((s) => (
                <div key={s.step} className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${s.status === 'done' ? 'bg-green-400' : s.status === 'active' ? 'bg-amber-400 animate-pulse' : 'bg-slate-700'}`} />
                  <span className="text-xs text-slate-400 flex-1">{s.step}</span>
                  <span className="text-[10px] text-slate-600 font-mono">{s.time}</span>
                </div>
              ))}
            </div>
          </div>
        </RevealSection>
      </section>

      {/* ══ B2B PLATFORM & BILLING (v28) ══ */}
      <section id="platform2" className="py-24 md:py-32 scroll-mt-24 border-t border-white/[0.04]">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="bg-slate-900/60 border border-white/[0.06] rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Org Dashboard</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-orange-500/10 text-orange-400 border border-orange-500/10">Admin</span>
              </div>
              <div className="space-y-3">
                <div className="bg-white/[0.02] rounded-lg px-4 py-3 border border-white/[0.04]">
                  <p className="text-xs text-slate-500 mb-1">Organisation</p>
                  <p className="text-sm font-semibold text-white">Marome Capital</p>
                  <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-500">
                    <span>12 members</span><span>Pro plan</span><span>Trial: 8 days left</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'Invite via CSV', icon: '📋' },
                    { label: 'Trial Banner',   icon: '⏳' },
                    { label: 'Billing History',icon: '💳' },
                    { label: 'Grace Period',   icon: '🔒' },
                  ].map((btn) => (
                    <div key={btn.label} className="bg-white/[0.02] rounded-lg px-3 py-2.5 border border-white/[0.04] flex items-center gap-2">
                      <span>{btn.icon}</span>
                      <span className="text-xs text-slate-400">{btn.label}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-white/[0.02] rounded-lg px-4 py-3 border border-white/[0.04]">
                  <p className="text-xs text-slate-500 mb-2">Recent Invites</p>
                  {['analyst@firm.co.za', 'trader@firm.co.za', 'pm@firm.co.za'].map((email) => (
                    <div key={email} className="flex items-center justify-between py-1">
                      <span className="text-xs text-slate-400">{email}</span>
                      <span className="text-[10px] text-green-400">Accepted</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-orange-400/70 mb-4">v28</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.05] mb-6">
                Built for teams.<br /><span className="rel-gradient-text">Not just traders.</span>
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                Full B2B platform with org management, invite flows, trial subscriptions, and a dedicated payments microservice. Teams can be onboarded via CSV bulk import and managed from a central admin panel.
              </p>
              <div className="space-y-3">
                {[
                  ['Payments microservice — PayStack USD, isolated Postgres'],
                  ['Org & subscription DB foundation — multi-tenant architecture'],
                  ['Invite-gated registration — no open sign-up by default'],
                  ['CSV bulk import — onboard entire teams at once'],
                  ['Offboarding grace period — 14-day access after cancellation'],
                  ['Trial banner + upgrade flow — contextual CTA in app'],
                  ['Org admin invite UI — manage members from settings'],
                  ['Billing history panel — invoices + subscription status'],
                  ['Registration flow UI — plan selection, pricing, conversion'],
                ].map(([text], i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={15} className="text-orange-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </RevealSection>
      </section>

      {/* ══ DYNAMIC PRICING (v29) ══ */}
      <section id="pricing" className="py-24 md:py-32 scroll-mt-24 border-t border-white/[0.04] bg-gradient-to-b from-teal-500/[0.04] to-transparent">
        <RevealSection className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-teal-400/70 mb-4">v29 — Latest</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.05] mb-6">
                Live prices.<br /><span className="rel-gradient-text">Admin control.</span>
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                Plan prices are now stored in the database and served live. Admins can update Pro and Premium pricing without a deployment — changes propagate in under 60 seconds. Existing subscribers are always protected with their locked rate.
              </p>
              <div className="space-y-3">
                {[
                  ['plan_prices table — DB-backed, admin-updateable without deploy'],
                  ['PayStack switched to USD — global pricing, not ZAR'],
                  ['locked_price_cents on subscriptions — existing users unaffected'],
                  ['Redis cache on prices endpoint — 60s TTL, invalidated on write'],
                  ['Admin Billing Prices panel — live price management in Settings'],
                  ['Register page fetches live prices — skeleton during load'],
                  ['Proxy routes in finance-dashboard — no direct payments calls'],
                  ['Confirmation dialog — explicit new-subscriber-only contract'],
                ].map(([text], i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={15} className="text-teal-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">{text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-900/60 border border-white/[0.06] rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign size={16} className="text-teal-400" />
                <span className="text-sm font-semibold text-white">Billing Prices</span>
                <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] bg-teal-500/10 text-teal-400 border border-teal-500/10">Admin only</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { plan: 'Pro',     price: '$99.00/mo',  cents: 9900 },
                  { plan: 'Premium', price: '$199.00/mo', cents: 19900 },
                ].map((p) => (
                  <div key={p.plan} className="bg-slate-700/50 rounded-lg p-4">
                    <p className="text-xs text-slate-400 mb-1">Current {p.plan} Price</p>
                    <p className="text-xl font-bold text-white">{p.price}</p>
                    <p className="text-[10px] text-slate-600 mt-1">{p.cents} cents</p>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <p className="text-xs text-slate-500">Set new prices below (USD). Changes affect <strong className="text-slate-300">new subscribers only</strong>.</p>
                <div className="grid grid-cols-2 gap-3">
                  {['Pro', 'Premium'].map((plan) => (
                    <div key={plan} className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                      <div className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-7 pr-3 py-2 text-slate-400 text-sm">{plan === 'Pro' ? '99.00' : '199.00'}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <div className="flex-1 h-px bg-white/[0.04]" />
                <span className="text-[10px] text-slate-600">Existing subscribers locked at their original rate</span>
                <div className="flex-1 h-px bg-white/[0.04]" />
              </div>
            </div>
          </div>
        </RevealSection>
      </section>

      {/* ══ PLANS ══ */}
      <section id="plans" className="py-24 border-t border-white/[0.04] scroll-mt-24">
        <RevealSection className="max-w-5xl mx-auto px-6">
          <p className="text-sm font-semibold tracking-widest uppercase text-slate-500 mb-4">Plans</p>
          <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white mb-6">
            One subscription. <span className="rel-gradient-text">An entire AI team.</span>
          </h2>
          <p className="text-slate-500 mb-16 max-w-xl">No competitor combines what my investments delivers. Competitors charge $254/month for a single AI scanner — you get a full ecosystem for less.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: 'Free Trial', tag: 'Explore everything', price: '14', priceLabel: 'days free',
                items: [[true,'Full platform access'], [true,'JSE + global markets'], [true,'Expert Chat (10 messages)'], [true,'AI analysis (5 reports)'], [true,'1 portfolio + 5 alerts'], [false,'Trading Analyst + EA'], [false,'Investment Banker']] as [boolean, string][],
              },
              {
                name: 'Pro', tag: 'Your AI analyst', featured: true, price: '$99', priceLabel: '/month', note: 'Cheaper than Trade Ideas ($127)',
                items: [[true,'Real-time data (73 exchanges)'], [true,'Expert Chat (unlimited)'], [true,'AI analysis (50/month)'], [true,'Trading Analyst + MQL5 EA'], [true,'Full screener + export'], [true,'Unlimited alerts & portfolios'], [true,'Strategy extraction'], [true,'MCP integration (13 tools)']] as [boolean, string][],
              },
              {
                name: 'Premium', tag: 'Your AI investment team', price: '$199', priceLabel: '/month', note: 'Replaces $343/mo in competing subs',
                items: [[true,'Everything in Pro'], [true,'Investment Banker (24/7)'], [true,'Self-learning AI'], [true,'MCP integration (13 tools)'], [true,'Multi-channel alerts'], [true,'Leads management'], [true,'Unlimited AI analyses'], [true,'Priority support']] as [boolean, string][],
              },
            ].map((tier) => (
              <div
                key={tier.name}
                className={`rounded-3xl p-8 ${tier.featured
                  ? 'bg-gradient-to-b from-white/[0.06] to-white/[0.02] border-2 border-teal-500/20 relative'
                  : 'bg-white/[0.02] border border-white/[0.04]'}`}
              >
                {tier.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-teal-500 to-indigo-500 text-[11px] font-bold text-white">MOST POPULAR</div>
                )}
                <h3 className="text-2xl font-bold text-white mb-1">{tier.name}</h3>
                <p className="text-slate-600 text-sm mb-4">{tier.tag}</p>
                <div className="mb-2">
                  <span className="text-4xl font-black text-white">{tier.price}</span>
                  <span className="text-slate-500 ml-1">{tier.priceLabel}</span>
                </div>
                {'note' in tier && tier.note && <p className="text-xs text-teal-400 font-medium mb-6">{tier.note}</p>}
                {!('note' in tier && tier.note) && <div className="mb-6" />}
                <ul className="space-y-3">
                  {tier.items.map(([ok, label], i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      {ok ? <CheckCircle2 size={15} className="text-teal-400 flex-shrink-0" /> : <X size={15} className="text-slate-800 flex-shrink-0" />}
                      <span className={ok ? 'text-slate-300' : 'text-slate-700'}>{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </RevealSection>
      </section>

      {/* ══ CTA ══ */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-teal-500/[0.08] to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-teal-500/[0.05] rounded-full blur-[150px] pointer-events-none" />
        <RevealSection className="relative max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-7xl font-black tracking-tight text-white mb-6">
            Start trading<br /><span className="rel-gradient-text">smarter.</span>
          </h2>
          <p className="text-lg text-slate-500 mb-12">Your AI team is ready. Are you?</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/register')}
              className="w-full sm:w-auto group px-8 py-4 bg-white text-black font-bold rounded-full text-[15px] transition-all hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(255,255,255,0.1)] flex items-center justify-center gap-2"
            >
              Create Account
              <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/welcome')}
              className="w-full sm:w-auto px-8 py-4 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] text-white font-semibold rounded-full text-[15px] transition-all flex items-center justify-center gap-2"
            >
              <Play size={14} />
              Learn More
            </button>
          </div>
        </RevealSection>
      </section>

      <footer className="border-t border-white/[0.04] py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-[13px] text-slate-700">
          <span>my investments</span>
          <span>v29.0 — Dynamic Pricing, B2B Platform, GitOps & AI Ops · 37 epics · 210 stories shipped</span>
        </div>
      </footer>
    </div>
  );
};

export default Releases;
